/**
 * @module components/Layout/DetailPanel
 * @description Panel lateral derecho de detalle completo de un control ISO 27002.
 *
 * Se abre como slide-in al seleccionar un control en `ControlsTable`. Permite
 * gestionar el ciclo de vida completo del control sin salir de la vista principal:
 *
 * ## Pestañas del panel
 * | Pestaña | Contenido |
 * |---------|-----------|
 * | Tareas | CRUD de tareas con checklist, prioridad, fecha límite y asignación |
 * | Evidencias | Carga de archivos (PDF, imágenes, videos) al Storage de Supabase |
 * | Auditoría | Trail de cambios con timestamps, usuario y valores anterior/nuevo |
 * | Asistente IA | `ACSButton` para abrir sesión ACS contextual al control |
 *
 * ## Comportamiento de permisos
 * - `admin`: puede crear/editar/eliminar tareas y evidencias
 * - `technical_referent`: puede crear/editar sus tareas asignadas
 * - `auditor` / `viewer`: solo lectura
 *
 * ## Actualización de progreso
 * Al completar tareas, el progreso del control se recalcula en tiempo real
 * con `updateTaskCompletion()` del DataContext.
 *
 * @see DataContext para las funciones CRUD de tareas y evidencias
 * @see ControlsTable que pasa el control seleccionado como prop
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  X, Calendar, User, CheckCircle, Upload, Download, Plus,
  Trash2, Clock, Save, AlertCircle, Mail, Building, FileText,
  File, Image, Video, FileArchive
} from 'lucide-react';
import { SelectedControl, Task, Evidence } from '../../types/iso';
import { useData } from '../../contexts/DataContext';
import { useAuth, usePermissions } from '../../contexts/AuthContext';
import { NotifyConfirmDialog } from '../common/NotifyConfirmDialog';
import { BulkTaskUploadModal } from '../Controls/BulkTaskUploadModal';
import { AuditGroup } from '../Audit/AuditEntryCard';
import { groupAuditEntriesByTime } from '../../utils/auditFormatters';
import { exportAuditToCSV, exportAuditToPDF } from '../../utils/auditExport';

// =====================================================
// INTERFACES Y TIPOS
// =====================================================

interface DetailPanelProps {
  /** Control ISO seleccionado para mostrar detalles */
  control: SelectedControl;
  /** Función para cerrar el panel de detalles */
  onClose: () => void;
  /** Funciones de notificación toast (opcional para backward compatibility) */
  toast?: {
    success: (message: string, description?: string) => void;
    error: (message: string, description?: string) => void;
    warning: (message: string, description?: string) => void;
    info: (message: string, description?: string) => void;
  };
}

/**
 * Formulario para crear/editar tareas
 */
interface TaskFormData {
  title: string;
  description: string;
  assigneeId: string;
  dueDate: string;
  priority: 'low' | 'medium' | 'high' | 'critical';
}

// =====================================================
// COMPONENTE PRINCIPAL
// =====================================================

export function DetailPanel({ control, onClose, toast }: DetailPanelProps) {
  // =====================================================
  // HOOKS Y CONTEXTOS
  // =====================================================
  
  const {
    technicalReferents,
    addTaskToControl,
    updateTask,
    deleteTask,
    addEvidenceToControl,
    updateEvidence,
    deleteEvidence,
    isLoading: dataLoading,
    error: dataError
  } = useData();
  
  const { user } = useAuth();
  const { canEditControls } = usePermissions();

  // =====================================================
  // ESTADOS LOCALES
  // =====================================================
  
  /** Pestaña activa en el panel de detalles */
  const [activeTab, setActiveTab] = useState<'tasks' | 'evidence' | 'audit'>('tasks');
  
  /** Estado para mostrar/ocultar formulario de nueva tarea */
  const [isAddingTask, setIsAddingTask] = useState(false);

  /** Estado para modal de carga masiva de tareas */
  const [isBulkUploadOpen, setIsBulkUploadOpen] = useState(false);
  
  /** ID de la tarea que se está editando */
  const [editingTask, setEditingTask] = useState<string | null>(null);
  
  /** Datos del formulario de tarea */
  const [taskForm, setTaskForm] = useState<TaskFormData>({
    title: '',
    description: '',
    assigneeId: '',
    dueDate: '',
    priority: 'medium'
  });
  
  /** Errores de validación del formulario */
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  
  /** Estado de carga local para operaciones */
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  /** Tareas del control actual */
  const [controlTasks, setControlTasks] = useState<Task[]>(control.tasks || []);

  /** Estados para carga de evidencias */
  const [isUploadingEvidence, setIsUploadingEvidence] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [evidenceName, setEvidenceName] = useState('');
  const [evidenceType, setEvidenceType] = useState<'document' | 'screenshot' | 'video' | 'other'>('document');
  const [deletingEvidenceId, setDeletingEvidenceId] = useState<string | null>(null);

  /** Diálogo de confirmación de notificación pendiente */
  const [pendingNotify, setPendingNotify] = useState<{
    recipientName: string;
    actionLabel: string;
    onConfirm: (notify: boolean) => void;
  } | null>(null);

  // =====================================================
  // CÁLCULOS DERIVADOS
  // =====================================================
  
  /** Número de tareas completadas */
  const completedTasks = controlTasks.filter(task => task.status === 'completed').length;
  
  /** Número total de tareas */
  const totalTasks = controlTasks.length;
  
  /** Porcentaje de progreso */
  const progress = totalTasks > 0 ? (completedTasks / totalTasks) * 100 : 0;
  
  /** Referentes técnicos activos disponibles para asignación */
  const availableReferents = technicalReferents.filter(r => r.isActive);

  // =====================================================
  // ESTADOS DE PAGINACIÓN
  // =====================================================

  /** Página actual para tareas */
  const [tasksPage, setTasksPage] = useState(1);
  const TASKS_PER_PAGE = 20;

  /** Página actual para evidencias */
  const [evidencePage, setEvidencePage] = useState(1);
  const EVIDENCE_PER_PAGE = 20;

  /** Página actual para auditoría */
  const [auditPage, setAuditPage] = useState(1);
  const AUDIT_PER_PAGE = 30;

  // =====================================================
  // CÁLCULOS MEMOIZADOS (OPTIMIZACIÓN DE PERFORMANCE)
  // =====================================================

  /** Tareas paginadas */
  const paginatedTasks = useMemo(() => {
    const startIndex = (tasksPage - 1) * TASKS_PER_PAGE;
    const endIndex = startIndex + TASKS_PER_PAGE;
    return controlTasks.slice(startIndex, endIndex);
  }, [controlTasks, tasksPage]);

  const totalTasksPages = Math.ceil(controlTasks.length / TASKS_PER_PAGE);

  /** Evidencias paginadas */
  const paginatedEvidence = useMemo(() => {
    const evidenceList = control.evidence || [];
    const startIndex = (evidencePage - 1) * EVIDENCE_PER_PAGE;
    const endIndex = startIndex + EVIDENCE_PER_PAGE;
    return evidenceList.slice(startIndex, endIndex);
  }, [control.evidence, evidencePage]);

  const totalEvidencePages = Math.ceil((control.evidence || []).length / EVIDENCE_PER_PAGE);

  /** Grupos de auditoría paginados (memoizado para mejor performance) */
  const groupedAuditEntries = useMemo(() => {
    return groupAuditEntriesByTime(control.auditTrail || []);
  }, [control.auditTrail]);

  const paginatedAuditGroups = useMemo(() => {
    const startIndex = (auditPage - 1) * AUDIT_PER_PAGE;
    const endIndex = startIndex + AUDIT_PER_PAGE;
    return groupedAuditEntries.slice(startIndex, endIndex);
  }, [groupedAuditEntries, auditPage]);

  const totalAuditPages = Math.ceil(groupedAuditEntries.length / AUDIT_PER_PAGE);

  // =====================================================
  // FUNCIONES AUXILIARES (DEBEN ESTAR ANTES DE useMemo)
  // =====================================================

  /**
   * Obtiene el ícono y color para un tipo de evidencia
   * @param type - Tipo de evidencia
   * @returns Ícono y clases CSS
   */
  const getEvidenceIcon = useCallback((type: string) => {
    switch (type) {
      case 'document':
        return {
          Icon: FileText,
          color: 'text-blue-600 dark:text-blue-400',
          bg: 'bg-blue-100 dark:bg-blue-900/30'
        };
      case 'screenshot':
        return {
          Icon: Image,
          color: 'text-green-600 dark:text-green-400',
          bg: 'bg-green-100 dark:bg-green-900/30'
        };
      case 'video':
        return {
          Icon: Video,
          color: 'text-purple-600 dark:text-purple-400',
          bg: 'bg-purple-100 dark:bg-purple-900/30'
        };
      default:
        return {
          Icon: File,
          color: 'text-gray-600 dark:text-gray-400',
          bg: 'bg-gray-100 dark:bg-gray-700'
        };
    }
  }, []);

  /** Pre-calcular iconos de evidencia (optimización) */
  const evidenceWithIcons = useMemo(() => {
    return (control.evidence || []).map(evidence => ({
      ...evidence,
      iconData: getEvidenceIcon(evidence.type)
    }));
  }, [control.evidence, getEvidenceIcon]);

  // =====================================================
  // EFECTOS
  // =====================================================
  
  /**
   * Actualiza las tareas locales cuando cambia el control
   */
  useEffect(() => {
    console.log(`🔍 [DETAIL PANEL] Control actualizado: ${control.code}`, {
      tasksCount: control.tasks?.length || 0,
      completedTasks: (control.tasks || []).filter(t => t.status === 'completed').length,
      auditTrailCount: control.auditTrail?.length || 0
    });
    
    setControlTasks(control.tasks || []);
  }, [control]);

  /**
   * Log de progreso para debugging y presentación
   */
  useEffect(() => {
    console.log(`📈 [PROGRESS CALCULATION] Control ${control.code}:`, {
      completed: completedTasks,
      total: totalTasks,
      progress: progress.toFixed(1) + '%',
      timestamp: new Date().toISOString()
    });
  }, [control.code, completedTasks, totalTasks, progress]);

  // =====================================================
  // FUNCIONES DE VALIDACIÓN
  // =====================================================
  
  /**
   * Valida los datos del formulario de tarea
   * @returns true si el formulario es válido
   */
  const validateTaskForm = useCallback((): boolean => {
    const errors: Record<string, string> = {};
    
    // Validar título
    if (!taskForm.title.trim()) {
      errors.title = 'El título es requerido';
    } else if (taskForm.title.trim().length < 3) {
      errors.title = 'El título debe tener al menos 3 caracteres';
    }
    
    // Validar responsable
    if (!taskForm.assigneeId) {
      errors.assigneeId = 'Debe seleccionar un responsable';
    }
    
    // Validar fecha de vencimiento
    if (!taskForm.dueDate) {
      errors.dueDate = 'La fecha de vencimiento es requerida';
    } else {
      const dueDate = new Date(taskForm.dueDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      if (dueDate < today) {
        errors.dueDate = 'La fecha de vencimiento no puede ser anterior a hoy';
      }
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }, [taskForm]);

  // =====================================================
  // FUNCIONES DE GESTIÓN DE TAREAS
  // =====================================================
  
  /**
   * Maneja el cambio de estado de una tarea
   * @param taskId - ID de la tarea
   * @param newStatus - Nuevo estado de la tarea
   */
  const handleTaskStatusChange = useCallback(async (taskId: string, newStatus: string) => {
    const task = controlTasks.find(t => t.id === taskId);
    if (!task) {
      console.error(`❌ [TASK STATUS] Tarea ${taskId} no encontrada`);
      return;
    }

    const oldStatus = task.status;
    
    console.log(`🔄 [TASK STATUS CHANGE] Iniciando cambio de estado`);
    console.log(`   📋 Tarea: "${task.title}"`);
    console.log(`   🔄 Estado: ${oldStatus} → ${newStatus}`);
    console.log(`   👤 Responsable: ${task.assignee.name}`);
    console.log(`   🎯 Control: ${control.code}`);
    console.log(`   👨‍💼 Usuario: ${user?.name}`);

    try {
      setIsSubmitting(true);

      // Preparar datos de actualización
      const updates: Partial<Task> = {
        status: newStatus as any,
        completedAt: newStatus === 'completed' ? new Date().toISOString() : undefined
      };

      // Actualizar tarea en la base de datos
      await updateTask(taskId, updates);

      // Actualizar estado local inmediatamente para mejor UX
      setControlTasks(prevTasks =>
        prevTasks.map(t =>
          t.id === taskId
            ? { ...t, ...updates }
            : t
        )
      );

      console.log(`✅ [TASK STATUS] Estado actualizado exitosamente`);
      console.log(`📊 [PROGRESS UPDATE] Nuevo progreso del control ${control.code}`);

      // Mostrar notificación de éxito
      if (toast) {
        const statusLabels: Record<string, string> = {
          pending: 'Pendiente',
          in_progress: 'En Progreso',
          completed: 'Completado'
        };
        toast.success(
          'Estado actualizado',
          `"${task.title}" ahora está en estado: ${statusLabels[newStatus] || newStatus}`
        );
      }

    } catch (error) {
      console.error(`❌ [TASK STATUS] Error actualizando estado:`, error);

      // Mostrar notificación de error
      if (toast) {
        toast.error(
          'Error al actualizar el estado',
          'No se pudo cambiar el estado de la tarea. Por favor intente nuevamente.'
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [controlTasks, control.code, user?.name, updateTask]);

  /**
   * Maneja la creación de una nueva tarea
   */
  const handleAddTask = useCallback(async () => {
    console.log(`➕ [CREATE TASK] Iniciando creación de tarea`);
    console.log(`   📋 Título: "${taskForm.title}"`);
    console.log(`   🎯 Control: ${control.code}`);
    console.log(`   👤 Usuario: ${user?.name}`);

    // Validar formulario
    if (!validateTaskForm()) {
      console.log(`❌ [CREATE TASK] Validación fallida:`, formErrors);
      return;
    }

    const assignee = availableReferents.find(r => r.id === taskForm.assigneeId);
    if (!assignee) {
      console.error(`❌ [CREATE TASK] Responsable no encontrado: ${taskForm.assigneeId}`);
      return;
    }

    console.log(`   👤 Responsable: ${assignee.name} (${assignee.email})`);
    console.log(`   📅 Fecha límite: ${taskForm.dueDate}`);
    console.log(`   🚨 Prioridad: ${taskForm.priority}`);

    const newTaskData: Omit<Task, 'id' | 'createdAt' | 'updatedAt'> = {
      title: taskForm.title.trim(),
      description: taskForm.description.trim(),
      status: 'pending',
      assignee,
      dueDate: taskForm.dueDate,
      completedAt: undefined,
      checklist: [],
      priority: taskForm.priority,
      estimatedHours: 0,
      actualHours: 0,
      emailSent: false,
      emailSentAt: undefined
    };

    // Mostrar diálogo de confirmación de notificación
    setPendingNotify({
      recipientName: assignee.name,
      actionLabel: 'crear la tarea',
      onConfirm: async (notify) => {
        setPendingNotify(null);
        setIsSubmitting(true);
        try {
          await addTaskToControl(control.id, newTaskData, notify);
          console.log(`✅ [CREATE TASK] Tarea creada exitosamente`);

          if (toast) {
            toast.success(
              'Tarea creada exitosamente',
              `Se asignó "${taskForm.title}" a ${assignee.name}`
            );
          }

          setTaskForm({ title: '', description: '', assigneeId: '', dueDate: '', priority: 'medium' });
          setFormErrors({});
          setIsAddingTask(false);
        } catch (error) {
          console.error(`❌ [CREATE TASK] Error creando tarea:`, error);
          if (toast) {
            toast.error(
              'Error al crear la tarea',
              'Por favor revise los datos e intente nuevamente. Si el problema persiste, contacte al administrador.'
            );
          }
        } finally {
          setIsSubmitting(false);
        }
      }
    });
  }, [taskForm, validateTaskForm, formErrors, availableReferents, control.id, control.code, user?.name, addTaskToControl, setPendingNotify]);

  /**
   * Maneja la eliminación de una tarea
   * @param taskId - ID de la tarea a eliminar
   */
  const handleDeleteTask = useCallback(async (taskId: string) => {
    const task = controlTasks.find(t => t.id === taskId);
    if (!task) return;

    const confirmMessage = `¿Estás seguro de que deseas eliminar la tarea "${task.title}"?`;
    if (!window.confirm(confirmMessage)) return;

    console.log(`🗑️ [DELETE TASK] Eliminando tarea ${taskId}: ${task.title}`);

    try {
      setIsSubmitting(true);

      // Eliminar tarea de la base de datos
      await deleteTask(taskId);

      // Actualizar estado local
      setControlTasks(prevTasks => prevTasks.filter(t => t.id !== taskId));

      console.log(`✅ [DELETE TASK] Tarea eliminada exitosamente`);

      // Mostrar notificación de éxito
      if (toast) {
        toast.success(
          'Tarea eliminada',
          `La tarea "${task.title}" fue eliminada exitosamente`
        );
      }

    } catch (error) {
      console.error(`❌ [DELETE TASK] Error eliminando tarea:`, error);

      // Mostrar notificación de error
      if (toast) {
        toast.error(
          'Error al eliminar la tarea',
          'No se pudo eliminar la tarea. Por favor intente nuevamente.'
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [controlTasks, deleteTask]);

  /**
   * Cancela la creación/edición de tarea
   */
  const handleCancelTaskForm = useCallback(() => {
    console.log(`❌ [CANCEL TASK] Cancelando formulario de tarea`);

    setTaskForm({
      title: '',
      description: '',
      assigneeId: '',
      dueDate: '',
      priority: 'medium'
    });
    setFormErrors({});
    setIsAddingTask(false);
    setEditingTask(null);
  }, []);

  // =====================================================
  // HANDLERS DE EVIDENCIAS
  // =====================================================

  /**
   * Maneja la selección de archivo para evidencia
   */
  const handleFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    console.log(`📎 [EVIDENCE] Archivo seleccionado:`, file.name, file.size, file.type);

    // Validar tamaño del archivo (10MB máximo)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      if (toast) {
        toast.error(
          'Archivo demasiado grande',
          `El archivo pesa ${(file.size / 1024 / 1024).toFixed(2)} MB. El tamaño máximo permitido es 10 MB.`
        );
      } else {
        alert(`El archivo es demasiado grande. Tamaño máximo: 10 MB`);
      }
      // Limpiar input
      event.target.value = '';
      return;
    }

    // Tipos de archivo permitidos
    const allowedTypes = [
      // Documentos
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'text/plain',
      'text/csv',
      // Imágenes
      'image/jpeg',
      'image/png',
      'image/gif',
      'image/webp',
      'image/bmp',
      // Videos
      'video/mp4',
      'video/mpeg',
      'video/quicktime',
      'video/x-msvideo',
      'video/webm',
      // Archivos comprimidos
      'application/zip',
      'application/x-rar-compressed',
      'application/x-7z-compressed',
    ];

    if (!allowedTypes.includes(file.type)) {
      if (toast) {
        toast.warning(
          'Tipo de archivo no común',
          `El archivo "${file.name}" tiene un tipo no común (${file.type || 'desconocido'}). Asegúrese de que sea un archivo válido.`
        );
      }
    }

    setSelectedFile(file);
    setEvidenceName(file.name);
  }, [toast]);

  /**
   * Maneja la subida de evidencia
   */
  const handleUploadEvidence = useCallback(async () => {
    if (!selectedFile) {
      alert('Por favor selecciona un archivo');
      return;
    }

    if (!evidenceName.trim()) {
      alert('Por favor ingresa un nombre para la evidencia');
      return;
    }

    console.log(`📤 [EVIDENCE] Iniciando subida de evidencia: ${evidenceName}`);

    try {
      setIsUploadingEvidence(true);

      await addEvidenceToControl(control.id, selectedFile, {
        name: evidenceName,
        type: evidenceType,
      });

      console.log(`✅ [EVIDENCE] Evidencia subida exitosamente`);

      // Limpiar formulario
      setSelectedFile(null);
      setEvidenceName('');
      setEvidenceType('document');

      // Resetear input file
      const fileInput = document.getElementById('evidence-file-input') as HTMLInputElement;
      if (fileInput) fileInput.value = '';

      // Mostrar notificación de éxito
      if (toast) {
        toast.success(
          'Evidencia subida exitosamente',
          `"${evidenceName}" (${(selectedFile.size / 1024 / 1024).toFixed(2)} MB) se cargó correctamente`
        );
      } else {
        alert('Evidencia subida exitosamente');
      }

    } catch (error) {
      console.error(`❌ [EVIDENCE] Error subiendo evidencia:`, error);

      // Mostrar notificación de error
      if (toast) {
        toast.error(
          'Error al subir la evidencia',
          'Por favor verifique el archivo e intente nuevamente. Tamaño máximo: 10 MB.'
        );
      } else {
        alert('Error al subir la evidencia. Por favor intenta nuevamente.');
      }
    } finally {
      setIsUploadingEvidence(false);
    }
  }, [selectedFile, evidenceName, evidenceType, control.id, addEvidenceToControl]);

  /**
   * Cancela la subida de evidencia
   */
  const handleCancelEvidence = useCallback(() => {
    setSelectedFile(null);
    setEvidenceName('');
    setEvidenceType('document');

    const fileInput = document.getElementById('evidence-file-input') as HTMLInputElement;
    if (fileInput) fileInput.value = '';
  }, []);

  /**
   * Maneja la descarga de una evidencia
   */
  const handleDownloadEvidence = useCallback(async (evidence: Evidence) => {
    console.log(`📥 [EVIDENCE] Iniciando descarga de evidencia: ${evidence.name}`);

    try {
      // Obtener la URL del archivo
      const fileUrl = evidence.url;

      if (!fileUrl) {
        throw new Error('URL de archivo no disponible');
      }

      // Crear un enlace temporal para descargar
      const link = document.createElement('a');
      link.href = fileUrl;
      link.target = '_blank';
      link.download = evidence.name;

      // Agregar al DOM, hacer click y remover
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      console.log(`✅ [EVIDENCE] Descarga iniciada exitosamente`);

      // Mostrar notificación de éxito
      if (toast) {
        toast.success(
          'Descarga iniciada',
          `"${evidence.name}" se está descargando`
        );
      }

    } catch (error) {
      console.error(`❌ [EVIDENCE] Error descargando evidencia:`, error);

      // Mostrar notificación de error
      if (toast) {
        toast.error(
          'Error al descargar',
          'No se pudo descargar la evidencia. Por favor intente nuevamente.'
        );
      } else {
        alert('Error al descargar la evidencia. Por favor intente nuevamente.');
      }
    }
  }, [toast]);

  /**
   * Maneja la eliminación de una evidencia
   */
  const handleDeleteEvidence = useCallback(async (evidence: Evidence) => {
    // Confirmación
    const confirmMessage = `¿Estás seguro de que deseas eliminar la evidencia "${evidence.name}"?\n\nEsta acción no se puede deshacer.`;
    if (!window.confirm(confirmMessage)) return;

    console.log(`🗑️ [EVIDENCE] Iniciando eliminación de evidencia: ${evidence.name}`);

    try {
      setDeletingEvidenceId(evidence.id);

      await deleteEvidence(evidence.id, control.id);

      console.log(`✅ [EVIDENCE] Evidencia eliminada exitosamente`);

      // Mostrar notificación de éxito
      if (toast) {
        toast.success(
          'Evidencia eliminada',
          `"${evidence.name}" fue eliminada exitosamente`
        );
      } else {
        alert('Evidencia eliminada exitosamente');
      }

    } catch (error) {
      console.error(`❌ [EVIDENCE] Error eliminando evidencia:`, error);

      // Mostrar notificación de error
      if (toast) {
        toast.error(
          'Error al eliminar',
          'No se pudo eliminar la evidencia. Por favor intente nuevamente.'
        );
      } else {
        alert('Error al eliminar la evidencia. Por favor intente nuevamente.');
      }
    } finally {
      setDeletingEvidenceId(null);
    }
  }, [control.id, deleteEvidence, toast]);

  /**
   * Maneja el cambio de estado de una evidencia
   * @param evidenceId - ID de la evidencia
   * @param newStatus - Nuevo estado de la evidencia
   */
  const handleEvidenceStatusChange = useCallback(async (evidenceId: string, newStatus: 'pending' | 'approved' | 'rejected') => {
    console.log(`📝 [EVIDENCE] Cambiando estado de evidencia ${evidenceId} a: ${newStatus}`);

    const doUpdate = async (notify: boolean) => {
      try {
        await updateEvidence(evidenceId, { status: newStatus }, notify);
        console.log(`✅ [EVIDENCE] Estado actualizado exitosamente`);
        if (toast) {
          const statusLabels: Record<string, string> = { pending: 'Pendiente', approved: 'Aprobada', rejected: 'Rechazada' };
          toast.success('Estado actualizado', `La evidencia fue marcada como: ${statusLabels[newStatus]}`);
        }
      } catch (error) {
        console.error(`❌ [EVIDENCE] Error actualizando estado:`, error);
        if (toast) {
          toast.error('Error al actualizar', 'No se pudo cambiar el estado de la evidencia. Por favor intente nuevamente.');
        } else {
          alert('Error al cambiar el estado de la evidencia. Por favor intente nuevamente.');
        }
      }
    };

    if (newStatus === 'approved' || newStatus === 'rejected') {
      const evidence = control.evidence?.find(e => e.id === evidenceId);
      setPendingNotify({
        recipientName: evidence?.uploadedBy?.name ?? 'el usuario',
        actionLabel: 'cambiar el estado de la evidencia',
        onConfirm: async (notify) => {
          setPendingNotify(null);
          await doUpdate(notify);
        }
      });
    } else {
      await doUpdate(true);
    }
  }, [updateEvidence, control.evidence, toast, setPendingNotify]);

  // =====================================================
  // FUNCIONES DE UTILIDAD
  // =====================================================

  /**
   * Obtiene el color CSS para un estado de tarea
   * @param status - Estado de la tarea
   * @returns Clases CSS para el estado
   */
  const getStatusColor = useCallback((status: string): string => {
    switch (status) {
      case 'completed': 
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
      case 'in_progress': 
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
      case 'review': 
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300';
      case 'not_started': 
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
      default: 
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
    }
  }, []);

  /**
   * Obtiene el texto legible para un estado
   * @param status - Estado de la tarea
   * @returns Texto del estado en español
   */
  const getStatusText = useCallback((status: string): string => {
    switch (status) {
      case 'completed': return 'Completado';
      case 'in_progress': return 'En Progreso';
      case 'review': return 'En Revisión';
      case 'not_started': return 'No Iniciado';
      case 'pending': return 'Pendiente';
      default: return 'No Conforme';
    }
  }, []);

  /**
   * Obtiene el color CSS para una prioridad
   * @param priority - Prioridad de la tarea
   * @returns Clases CSS para la prioridad
   */
  const getPriorityColor = useCallback((priority: string): string => {
    switch (priority) {
      case 'critical': 
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
      case 'high': 
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300';
      case 'medium': 
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300';
      default: 
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
    }
  }, []);

  /**
   * Obtiene el texto legible para una prioridad
   * @param priority - Prioridad de la tarea
   * @returns Texto de la prioridad en español
   */
  const getPriorityText = useCallback((priority: string): string => {
    switch (priority) {
      case 'critical': return 'Crítica';
      case 'high': return 'Alta';
      case 'medium': return 'Media';
      default: return 'Baja';
    }
  }, []);

  // =====================================================
  // RENDERIZADO DEL COMPONENTE
  // =====================================================

  return (
    <div className="w-96 h-full bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 flex flex-col">
      {/* ===== ENCABEZADO DEL PANEL ===== */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            {/* Código del control */}
            <div className="text-sm text-blue-600 dark:text-blue-400 font-medium mb-1">
              {control.code}
            </div>
            
            {/* Título del control */}
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              {control.title}
            </h3>
            
            {/* Breadcrumb de navegación */}
            <nav className="text-xs text-gray-500 dark:text-gray-400">
              {control.breadcrumb.join(' > ')}
            </nav>
          </div>
          
          {/* Botón Cerrar */}
          <div className="flex items-center gap-1">
            <button
              onClick={onClose}
              className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
              aria-label="Cerrar panel de detalles"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
        </div>

        {/* ===== INFORMACIÓN DE PROGRESO ===== */}
        <div className="mt-4">
          {/* Barra de progreso */}
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="text-gray-700 dark:text-gray-300">Progreso de Tareas</span>
            <span className="text-gray-900 dark:text-white font-medium">
              {completedTasks}/{totalTasks} completadas
            </span>
          </div>
          
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mb-3">
            <div
              className="bg-blue-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
              role="progressbar"
              aria-valuenow={progress}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label={`Progreso: ${progress.toFixed(1)}%`}
            />
          </div>
          
          {/* Responsables asignados */}
          {control.assignedReferents && control.assignedReferents.length > 0 && (
            <div className="mt-3">
              <span className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">
                Responsables:
              </span>
              <div className="flex flex-wrap gap-1">
                {control.assignedReferents.map((referent) => (
                  <div 
                    key={referent.id}
                    className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300"
                    title={`${referent.name} - ${referent.email}`}
                  >
                    <User className="w-3 h-3 mr-1" />
                    {referent.name}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ===== PESTAÑAS DE NAVEGACIÓN ===== */}
      <div className="flex border-b border-gray-200 dark:border-gray-700">
        {[
          { 
            key: 'tasks', 
            label: 'Tareas', 
            count: controlTasks.length,
            icon: CheckCircle 
          },
          { 
            key: 'evidence', 
            label: 'Evidencias', 
            count: (control.evidence || []).length,
            icon: Upload 
          },
          { 
            key: 'audit', 
            label: 'Auditoría', 
            count: (control.auditTrail || []).length,
            icon: Clock 
          }
        ].map((tab) => {
          const IconComponent = tab.icon;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key as any)}
              className={`flex-1 px-3 py-2 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                activeTab === tab.key
                  ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400 bg-blue-50 dark:bg-blue-900/20'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
              aria-label={`Ver ${tab.label.toLowerCase()}`}
            >
              <div className="flex items-center justify-center space-x-1">
                <IconComponent className="w-4 h-4" />
                <span>{tab.label}</span>
                <span className="text-xs bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300 px-1.5 py-0.5 rounded-full">
                  {tab.count}
                </span>
              </div>
            </button>
          );
        })}
      </div>

      {/* ===== CONTENIDO DE LAS PESTAÑAS ===== */}
      <div className="flex-1 overflow-auto p-4">
        {/* PESTAÑA DE TAREAS */}
        {activeTab === 'tasks' && (
          <>
          <div className="space-y-4">
            {/* Botones para agregar tarea individual o carga masiva */}
            {canEditControls && !isAddingTask && (
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    console.log(`➕ [UI] Iniciando creación de nueva tarea para control ${control.code}`);
                    setIsAddingTask(true);
                  }}
                  disabled={isSubmitting || dataLoading}
                  className="flex-1 flex items-center justify-center space-x-2 p-3 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-gray-600 dark:text-gray-400 hover:border-blue-500 hover:text-blue-600 dark:hover:text-blue-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-blue-500"
                  aria-label="Agregar nueva tarea"
                >
                  <Plus className="w-4 h-4" />
                  <span className="text-sm font-medium">Agregar Tarea</span>
                </button>
                <button
                  onClick={() => {
                    console.log(`📥 [UI] Abriendo carga masiva para control ${control.code}`);
                    setIsBulkUploadOpen(true);
                  }}
                  disabled={isSubmitting || dataLoading}
                  className="flex items-center justify-center space-x-2 px-4 py-3 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-gray-600 dark:text-gray-400 hover:border-green-500 hover:text-green-600 dark:hover:text-green-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-green-500"
                  aria-label="Carga masiva de tareas"
                  title="Cargar tareas desde CSV"
                >
                  <Upload className="w-4 h-4" />
                  <span className="text-sm font-medium">Carga Masiva</span>
                </button>
              </div>
            )}

            {/* Formulario para nueva tarea */}
            {isAddingTask && (
              <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-700">
                <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3 flex items-center">
                  <Plus className="w-4 h-4 mr-2" />
                  Nueva Tarea para {control.code}
                </h4>
                
                <div className="space-y-3">
                  {/* Campo: Título */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Título de la tarea *
                    </label>
                    <input
                      type="text"
                      placeholder="Ej: Implementar política de contraseñas"
                      value={taskForm.title}
                      onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })}
                      className={`w-full text-sm border rounded px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                        formErrors.title 
                          ? 'border-red-300 dark:border-red-600' 
                          : 'border-gray-300 dark:border-gray-600'
                      }`}
                      disabled={isSubmitting}
                      autoFocus
                    />
                    {formErrors.title && (
                      <p className="text-xs text-red-600 dark:text-red-400 mt-1 flex items-center">
                        <AlertCircle className="w-3 h-3 mr-1" />
                        {formErrors.title}
                      </p>
                    )}
                  </div>

                  {/* Campo: Descripción */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Descripción
                    </label>
                    <textarea
                      placeholder="Descripción detallada de la tarea..."
                      value={taskForm.description}
                      onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })}
                      className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                      rows={2}
                      disabled={isSubmitting}
                    />
                  </div>

                  {/* Campo: Responsable */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Responsable *
                    </label>
                    <select
                      value={taskForm.assigneeId}
                      onChange={(e) => setTaskForm({ ...taskForm, assigneeId: e.target.value })}
                      className={`w-full text-sm border rounded px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                        formErrors.assigneeId 
                          ? 'border-red-300 dark:border-red-600' 
                          : 'border-gray-300 dark:border-gray-600'
                      }`}
                      disabled={isSubmitting}
                    >
                      <option value="">Seleccionar responsable</option>
                      {availableReferents.map(referent => (
                        <option key={referent.id} value={referent.id}>
                          {referent.name} - {referent.unit}
                        </option>
                      ))}
                    </select>
                    {formErrors.assigneeId && (
                      <p className="text-xs text-red-600 dark:text-red-400 mt-1 flex items-center">
                        <AlertCircle className="w-3 h-3 mr-1" />
                        {formErrors.assigneeId}
                      </p>
                    )}
                  </div>

                  {/* Campos: Fecha y Prioridad */}
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Fecha límite *
                      </label>
                      <input
                        type="date"
                        value={taskForm.dueDate}
                        onChange={(e) => setTaskForm({ ...taskForm, dueDate: e.target.value })}
                        min={new Date().toISOString().split('T')[0]}
                        className={`w-full text-sm border rounded px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                          formErrors.dueDate 
                            ? 'border-red-300 dark:border-red-600' 
                            : 'border-gray-300 dark:border-gray-600'
                        }`}
                        disabled={isSubmitting}
                      />
                      {formErrors.dueDate && (
                        <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                          {formErrors.dueDate}
                        </p>
                      )}
                    </div>
                    
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Prioridad
                      </label>
                      <select
                        value={taskForm.priority}
                        onChange={(e) => setTaskForm({ ...taskForm, priority: e.target.value as any })}
                        className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                        disabled={isSubmitting}
                      >
                        <option value="low">Baja</option>
                        <option value="medium">Media</option>
                        <option value="high">Alta</option>
                        <option value="critical">Crítica</option>
                      </select>
                    </div>
                  </div>

                  {/* Botones de acción */}
                  <div className="flex space-x-2 pt-2">
                    <button
                      onClick={handleAddTask}
                      disabled={isSubmitting || !taskForm.title.trim() || !taskForm.assigneeId || !taskForm.dueDate}
                      className="flex-1 px-3 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center space-x-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          <span>Creando...</span>
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4" />
                          <span>Crear Tarea</span>
                        </>
                      )}
                    </button>
                    
                    <button
                      onClick={handleCancelTaskForm}
                      disabled={isSubmitting}
                      className="px-3 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-sm rounded hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500"
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Lista de tareas existentes */}
            {controlTasks.length === 0 && !isAddingTask ? (
              <div className="text-center py-8">
                <CheckCircle className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                  No hay tareas creadas para este control
                </p>
                <p className="text-xs text-gray-400 dark:text-gray-500">
                  Las tareas ayudan a organizar el trabajo necesario para cumplir con el control
                </p>
              </div>
            ) : (
              paginatedTasks.map((task) => (
                <div 
                  key={task.id} 
                  className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:shadow-sm transition-shadow"
                >
                  {/* Encabezado de la tarea */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                        {task.title}
                      </h4>
                      
                      {/* Etiquetas de prioridad y estado */}
                      <div className="flex items-center space-x-2 mt-1">
                        <span className={`px-2 py-1 text-xs rounded-full font-medium ${getPriorityColor(task.priority)}`}>
                          {getPriorityText(task.priority)}
                        </span>
                        
                        {task.status === 'completed' && (
                          <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300 font-medium flex items-center">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Completada
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {/* Controles de estado y acciones */}
                    <div className="flex items-center space-x-2 ml-4">
                      {/* Selector de estado */}
                      {canEditControls && (
                        <select
                          value={task.status}
                          onChange={(e) => {
                            console.log(`🎯 [UI] Cambio de estado solicitado por usuario`);
                            console.log(`   📋 Tarea: "${task.title}"`);
                            console.log(`   🔄 Cambio: ${task.status} → ${e.target.value}`);
                            console.log(`   👤 Usuario: ${user?.name}`);
                            handleTaskStatusChange(task.id, e.target.value);
                          }}
                          disabled={isSubmitting}
                          className="text-xs px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 cursor-pointer hover:border-blue-500 transition-colors disabled:opacity-50"
                          title="Cambiar estado de la tarea"
                        >
                          <option value="pending">Pendiente</option>
                          <option value="in_progress">En Progreso</option>
                          <option value="completed">Completado</option>
                        </select>
                      )}
                      
                      {/* Estado solo lectura */}
                      {!canEditControls && (
                        <span className={`px-2 py-1 text-xs rounded-full font-medium ${getStatusColor(task.status)}`}>
                          {getStatusText(task.status)}
                        </span>
                      )}
                      
                      {/* Botón de eliminar */}
                      {canEditControls && (
                        <button
                          onClick={() => handleDeleteTask(task.id)}
                          disabled={isSubmitting}
                          className="p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-400 rounded transition-colors disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-red-500"
                          title="Eliminar tarea"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  </div>
                  
                  {/* Descripción de la tarea */}
                  {task.description && (
                    <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">
                      {task.description}
                    </p>
                  )}

                  {/* Información del responsable y fechas */}
                  <div className="flex items-center text-xs text-gray-500 dark:text-gray-400 mb-3 space-x-4">
                    {/* Responsable */}
                    <div className="flex items-center space-x-1">
                      <User className="w-3 h-3" />
                      <span>{task.assignee.name}</span>
                    </div>
                    
                    {/* Fecha límite */}
                    <div className="flex items-center space-x-1">
                      <Calendar className="w-3 h-3" />
                      <span>{new Date(task.dueDate).toLocaleDateString()}</span>
                    </div>
                    
                    {/* Fecha de completado */}
                    {task.status === 'completed' && task.completedAt && (
                      <div className="flex items-center space-x-1 text-green-600 dark:text-green-400">
                        <CheckCircle className="w-3 h-3" />
                        <span>
                          Completado el {new Date(task.completedAt).toLocaleDateString()} 
                          a las {new Date(task.completedAt).toLocaleTimeString()}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Información adicional del responsable */}
                  <div className="bg-gray-50 dark:bg-gray-600 rounded p-2 text-xs">
                    <div className="flex items-center space-x-2 text-gray-600 dark:text-gray-300">
                      <Mail className="w-3 h-3" />
                      <span>{task.assignee.email}</span>
                    </div>
                    <div className="flex items-center space-x-2 text-gray-600 dark:text-gray-300 mt-1">
                      <Building className="w-3 h-3" />
                      <span>{task.assignee.unit}</span>
                    </div>
                    {task.emailSent && (
                      <div className="flex items-center space-x-2 text-green-600 dark:text-green-400 mt-1">
                        <Mail className="w-3 h-3" />
                        <span>
                          Email enviado {task.emailSentAt ? `el ${new Date(task.emailSentAt).toLocaleDateString()}` : ''}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}

            {/* Controles de paginación para tareas */}
            {controlTasks.length > TASKS_PER_PAGE && (
              <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  Mostrando {((tasksPage - 1) * TASKS_PER_PAGE) + 1}-{Math.min(tasksPage * TASKS_PER_PAGE, controlTasks.length)} de {controlTasks.length} tareas
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setTasksPage(prev => Math.max(1, prev - 1))}
                    disabled={tasksPage === 1}
                    className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Anterior
                  </button>
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    Página {tasksPage} de {totalTasksPages}
                  </span>
                  <button
                    onClick={() => setTasksPage(prev => Math.min(totalTasksPages, prev + 1))}
                    disabled={tasksPage === totalTasksPages}
                    className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Siguiente
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Modal de carga masiva */}
          {isBulkUploadOpen && (
            <BulkTaskUploadModal
              control={control}
              onClose={() => setIsBulkUploadOpen(false)}
              technicalReferents={availableReferents}
              addTaskToControl={addTaskToControl}
              toast={toast}
            />
          )}
          </>
        )}

        {/* PESTAÑA DE EVIDENCIAS */}
        {activeTab === 'evidence' && (
          <div className="space-y-3">
            {/* Formulario para subir nueva evidencia */}
            {canEditControls && (
              <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
                  Subir Nueva Evidencia
                </h4>

                {/* Input de archivo */}
                <div className="mb-3">
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Archivo *
                  </label>
                  <input
                    id="evidence-file-input"
                    type="file"
                    onChange={handleFileSelect}
                    disabled={isUploadingEvidence}
                    className="w-full text-sm text-gray-900 dark:text-white file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 dark:file:bg-blue-900/30 dark:file:text-blue-400 dark:hover:file:bg-blue-900/50 disabled:opacity-50"
                  />
                  {selectedFile && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Archivo seleccionado: {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                    </p>
                  )}
                </div>

                {/* Nombre de evidencia */}
                <div className="mb-3">
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Nombre de la evidencia *
                  </label>
                  <input
                    type="text"
                    value={evidenceName}
                    onChange={(e) => setEvidenceName(e.target.value)}
                    disabled={isUploadingEvidence}
                    placeholder="Ej: Política de seguridad firmada"
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-50"
                  />
                </div>

                {/* Tipo de evidencia */}
                <div className="mb-4">
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Tipo de evidencia
                  </label>
                  <select
                    value={evidenceType}
                    onChange={(e) => setEvidenceType(e.target.value as 'document' | 'screenshot' | 'video' | 'other')}
                    disabled={isUploadingEvidence}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-50"
                  >
                    <option value="document">Documento</option>
                    <option value="screenshot">Captura de pantalla</option>
                    <option value="video">Video</option>
                    <option value="other">Otro</option>
                  </select>
                </div>

                {/* Botones */}
                <div className="flex space-x-2">
                  <button
                    onClick={handleUploadEvidence}
                    disabled={isUploadingEvidence || !selectedFile || !evidenceName.trim()}
                    className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Upload className="w-4 h-4" />
                    <span>{isUploadingEvidence ? 'Subiendo...' : 'Subir Evidencia'}</span>
                  </button>
                  {selectedFile && (
                    <button
                      onClick={handleCancelEvidence}
                      disabled={isUploadingEvidence}
                      className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-sm font-medium rounded hover:bg-gray-300 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50"
                    >
                      Cancelar
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Lista de evidencias */}
            {(control.evidence || []).length === 0 ? (
              <div className="text-center py-8">
                <Upload className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                  No hay evidencias cargadas
                </p>
                <p className="text-xs text-gray-400 dark:text-gray-500">
                  Las evidencias documentan el cumplimiento del control
                </p>
              </div>
            ) : (
              paginatedEvidence.map((evidence) => {
                const { Icon, color, bg } = getEvidenceIcon(evidence.type);
                return (
                  <div key={evidence.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 hover:shadow-md transition-shadow">
                    <div className="flex items-start gap-3 mb-2">
                      {/* Ícono del tipo de archivo */}
                      <div className={`flex-shrink-0 w-10 h-10 rounded-lg ${bg} flex items-center justify-center`}>
                        <Icon className={`w-5 h-5 ${color}`} />
                      </div>

                      {/* Información del archivo */}
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-medium text-gray-900 dark:text-white truncate">
                          {evidence.name}
                        </h4>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {evidence.uploadedBy.name} • {new Date(evidence.uploadedAt).toLocaleDateString()}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {(evidence.size / 1024 / 1024).toFixed(2)} MB
                          </span>
                          <span className="text-xs text-gray-400">•</span>
                          <span className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                            {evidence.type === 'document' ? 'Documento' :
                             evidence.type === 'screenshot' ? 'Captura' :
                             evidence.type === 'video' ? 'Video' : 'Otro'}
                          </span>
                        </div>
                      </div>

                      {/* Botones de acción */}
                      <div className="flex-shrink-0 flex items-center gap-1">
                        <button
                          onClick={() => handleDownloadEvidence(evidence)}
                          disabled={deletingEvidenceId === evidence.id}
                          className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          title={`Descargar ${evidence.name}`}
                        >
                          <Download className="w-5 h-5 text-gray-500 hover:text-blue-600 dark:hover:text-blue-400" />
                        </button>

                        {canEditControls && (
                          <button
                            onClick={() => handleDeleteEvidence(evidence)}
                            disabled={deletingEvidenceId === evidence.id}
                            className="p-2 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            title={`Eliminar ${evidence.name}`}
                          >
                            {deletingEvidenceId === evidence.id ? (
                              <div className="w-5 h-5 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <Trash2 className="w-5 h-5 text-gray-500 hover:text-red-600 dark:hover:text-red-400" />
                            )}
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Estado de aprobación */}
                    <div className="flex justify-end">
                      {canEditControls ? (
                        <select
                          value={evidence.status}
                          onChange={(e) => handleEvidenceStatusChange(evidence.id, e.target.value as 'pending' | 'approved' | 'rejected')}
                          className={`px-2 py-1 text-xs rounded-full font-medium border-0 cursor-pointer transition-colors ${
                            evidence.status === 'approved'
                              ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                              : evidence.status === 'rejected'
                              ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                              : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
                          }`}
                          title="Cambiar estado de la evidencia"
                        >
                          <option value="pending">Pendiente</option>
                          <option value="approved">Aprobada</option>
                          <option value="rejected">Rechazada</option>
                        </select>
                      ) : (
                        <span className={`px-2 py-1 text-xs rounded-full font-medium ${
                          evidence.status === 'approved'
                            ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                            : evidence.status === 'rejected'
                            ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                            : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
                        }`}>
                          {evidence.status === 'approved' ? 'Aprobada' :
                           evidence.status === 'rejected' ? 'Rechazada' : 'Pendiente'}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })
            )}

            {/* Controles de paginación para evidencias */}
            {(control.evidence || []).length > EVIDENCE_PER_PAGE && (
              <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  Mostrando {((evidencePage - 1) * EVIDENCE_PER_PAGE) + 1}-{Math.min(evidencePage * EVIDENCE_PER_PAGE, (control.evidence || []).length)} de {(control.evidence || []).length} evidencias
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setEvidencePage(prev => Math.max(1, prev - 1))}
                    disabled={evidencePage === 1}
                    className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Anterior
                  </button>
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    Página {evidencePage} de {totalEvidencePages}
                  </span>
                  <button
                    onClick={() => setEvidencePage(prev => Math.min(totalEvidencePages, prev + 1))}
                    disabled={evidencePage === totalEvidencePages}
                    className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Siguiente
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* PESTAÑA DE AUDITORÍA */}
        {activeTab === 'audit' && (
          <div className="space-y-4">
            {/* Diferenciador visual - Contexto de Control Específico */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                  <FileText className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-100">
                    📋 Trazabilidad de Control Específico
                  </h3>
                  <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
                    Mostrando el historial completo de cambios, tareas, evidencias y asignaciones
                    realizadas únicamente en este control: <span className="font-semibold">{control.code} - {control.title}</span>
                  </p>
                </div>
              </div>
            </div>

            {/* Botones de Exportación */}
            {(control.auditTrail || []).length > 0 && (
              <div className="px-6 py-3 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 flex gap-3">
                <button
                  onClick={() => exportAuditToCSV(control.auditTrail || [], control.code, control.title)}
                  className="inline-flex items-center px-3 py-2 text-sm font-medium text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/30 hover:bg-blue-100 dark:hover:bg-blue-900/50 border border-blue-200 dark:border-blue-700 rounded-lg transition-colors"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Exportar CSV
                </button>
                <button
                  onClick={() => exportAuditToPDF(control.auditTrail || [], control.code, control.title)}
                  className="inline-flex items-center px-3 py-2 text-sm font-medium text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/30 hover:bg-red-100 dark:hover:bg-red-900/50 border border-red-200 dark:border-red-700 rounded-lg transition-colors"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Exportar PDF
                </button>
                <span className="text-xs text-gray-500 dark:text-gray-400 self-center">
                  ({control.auditTrail.length} entradas)
                </span>
              </div>
            )}

            {(control.auditTrail || []).length === 0 ? (
              <div className="text-center py-8">
                <Clock className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                  No hay actividad registrada
                </p>
                <p className="text-xs text-gray-400 dark:text-gray-500">
                  Aquí aparecerá el historial de cambios del control
                </p>
              </div>
            ) : (
              <>
                {/* Entradas agrupadas por tiempo */}
                {paginatedAuditGroups.map((group, index) => (
                  <AuditGroup
                    key={`${group.label}-${index}`}
                    label={group.label}
                    entries={group.entries}
                    compact={false}
                  />
                ))}

                {/* Controles de paginación para auditoría */}
                {groupedAuditEntries.length > AUDIT_PER_PAGE && (
                  <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      Mostrando {((auditPage - 1) * AUDIT_PER_PAGE) + 1}-{Math.min(auditPage * AUDIT_PER_PAGE, groupedAuditEntries.length)} de {groupedAuditEntries.length} entradas
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setAuditPage(prev => Math.max(1, prev - 1))}
                        disabled={auditPage === 1}
                        className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        Anterior
                      </button>
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        Página {auditPage} de {totalAuditPages}
                      </span>
                      <button
                        onClick={() => setAuditPage(prev => Math.min(totalAuditPages, prev + 1))}
                        disabled={auditPage === totalAuditPages}
                        className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        Siguiente
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* ===== INDICADOR DE ERROR ===== */}
      {dataError && (
        <div className="p-4 border-t border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20">
          <div className="flex items-center space-x-2 text-red-800 dark:text-red-200">
            <AlertCircle className="w-4 h-4" />
            <span className="text-sm">{dataError}</span>
          </div>
        </div>
      )}

      {/* ===== DIÁLOGO DE CONFIRMACIÓN DE NOTIFICACIÓN ===== */}
      {pendingNotify && (
        <NotifyConfirmDialog
          recipientName={pendingNotify.recipientName}
          actionLabel={pendingNotify.actionLabel}
          onConfirm={pendingNotify.onConfirm}
          onCancel={() => setPendingNotify(null)}
        />
      )}
    </div>
  );
}
